import { getTimeDefinitionService } from '../../../core/services/TimeDefinitionService.js';
import { accumulateStateFromMessages, lastRoundToActiveCharacters } from './state-utils.js';
import { militaryToBackgroundTimeSlot } from '../../hangout/shared/message-tree.js';
import { getScenarioIdFromHangoutConfig, normalizeHangoutScenarioConfig } from '../../hangout/shared/scenario-config.js';
import { isPlaceholderCharacterName, sanitizeCharacterName } from './character-name-utils.js';

const HANGOUT_ENTRYPOINTS = {
  RANKINGS_REGULAR_PUBLIC: 'rankings_regular_public',
  RANKINGS_REGULAR_PRIVATE: 'rankings_regular_private',
  RANKINGS_RESUME: 'rankings_resume'
};

const resolvePreferredCharacterName = ({ char, primaryCharacter, conversation, carouselCharacters }) => {
  const normalizedCharId = String(char?.id ?? '');
  const currentName = sanitizeCharacterName(char?.name);
  if (!isPlaceholderCharacterName(currentName, normalizedCharId)) return currentName;

  const isPrimaryCharacter = normalizedCharId && normalizedCharId === String(conversation?.characterId ?? '');
  const primaryName = sanitizeCharacterName(primaryCharacter?.name);
  if (isPrimaryCharacter && !isPlaceholderCharacterName(primaryName, normalizedCharId)) {
    return primaryName;
  }

  const conversationChar = conversation?.allCharacters?.find((entry) => String(entry?.id ?? '') === normalizedCharId);
  const conversationName = sanitizeCharacterName(conversationChar?.name);
  if (!isPlaceholderCharacterName(conversationName, normalizedCharId)) return conversationName;

  const carouselChar = carouselCharacters?.find((entry) => String(entry?.id ?? '') === normalizedCharId);
  const carouselName = sanitizeCharacterName(carouselChar?.name);
  if (!isPlaceholderCharacterName(carouselName, normalizedCharId)) return carouselName;

  return `Character ${normalizedCharId || 'Unknown'}`;
};

const resolveCharacterNames = (characters, primaryCharacter, conversation, carouselCharacters) => {
  return characters.map(char => {
    const characterName = resolvePreferredCharacterName({ char, primaryCharacter, conversation, carouselCharacters });

    return {
      id: char.id,
      name: characterName,
      outfitId: char.outfitId,
      sprite_state: char.sprite_state,
      goal: char.goal
    };
  });
};

export const buildResumeConfig = ({
  conversation,
  character,
  carouselCharacters,
  selectedOutfitId,
  normalizedMessages,
  scenarioTime,
  selectedScenario
}) => {
  let finalState = { loc_id: 0, timeOfDay: 'day', activeCharacters: [] };
  if (normalizedMessages?.length > 0) {
    finalState = accumulateStateFromMessages(normalizedMessages);
  } else {
    const previewLocation = Number.parseInt(String(conversation?.lastLocationId ?? ''), 10);
    const previewTime = Number.parseInt(String(conversation?.lastTime ?? ''), 10);
    const previewCharacters = lastRoundToActiveCharacters(conversation?.lastRound);
    finalState = {
      loc_id: Number.isFinite(previewLocation) ? previewLocation : 0,
      timeOfDay: (Number.isFinite(previewTime) ? militaryToBackgroundTimeSlot(previewTime) : null) || 'day',
      activeCharacters: previewCharacters.length > 0
        ? previewCharacters
        : (Array.isArray(conversation?.allCharacters) ? conversation.allCharacters : [])
    };
  }

  // Use accumulated state's timeOfDay if available, otherwise fall back to scenario time
  let timeOfDay = finalState.timeOfDay || 'day';
  if (!finalState.timeOfDay && scenarioTime) {
    timeOfDay = getTimeDefinitionService().getTimeOfDayFromNumericSlot(scenarioTime);
  }

  const hangoutConfig = {
    characterId: conversation.characterId,
    scenarioId: conversation.scenarioId,
    outfitId: selectedOutfitId || 0,
    conversationId: conversation.conversationId,
    isNewConversation: false,
    isResuming: true,
    greetingVariation: Number.parseInt(String(conversation.greetingVariation ?? 0), 10) || 0,
    greetingVariationCount: Number.parseInt(String(conversation.greetingVariationCount ?? 1), 10) || 1,
    entrypoint: HANGOUT_ENTRYPOINTS.RANKINGS_RESUME,
    entryMode: 'resume',
    name: character?.name,
    loc_id: String(finalState.loc_id || 0),
    timeOfDay: timeOfDay
  };

  if (selectedScenario && typeof selectedScenario === 'object') {
    hangoutConfig.selectedScenario = { ...selectedScenario };
  } else if (conversation.scenarioId) {
    hangoutConfig.selectedScenario = { id: String(conversation.scenarioId) };
  }

  // Use accumulated state characters if available
  if (finalState.activeCharacters?.length > 0) {
    hangoutConfig.allCharacters = resolveCharacterNames(
      finalState.activeCharacters,
      character,
      conversation,
      carouselCharacters
    );
    hangoutConfig.isMultiCharacter = finalState.activeCharacters.length > 1;
    hangoutConfig.characterCount = finalState.activeCharacters.length;
    hangoutConfig.primaryCharacterIndex = 0;
  } else if (conversation.isMultiCharacter && conversation.allCharacters?.length > 1) {
    // Fall back to conversation data
    let charactersToStore = conversation.allCharacters;

    if (carouselCharacters?.length > 1) {
      charactersToStore = carouselCharacters.map(carouselChar => {
        const originalChar = conversation.allCharacters.find(c => String(c.id) === String(carouselChar.id));
        const resolvedName = resolvePreferredCharacterName({
          char: {
            id: carouselChar.id,
            name: carouselChar.name || originalChar?.name
          },
          primaryCharacter: character,
          conversation,
          carouselCharacters
        });
        return {
          ...(originalChar || {}),
          id: carouselChar.id,
          name: resolvedName,
          rank: carouselChar.rank ?? originalChar?.rank,
          outfitId: carouselChar.outfitId ?? originalChar?.outfitId,
          modalImage: carouselChar.modalImage ?? originalChar?.modalImage,
          lastSpriteState: carouselChar.lastSpriteState || carouselChar.lastEmotion || originalChar?.lastSpriteState || originalChar?.lastEmotion,
          lastAction: carouselChar.lastAction ?? originalChar?.lastAction
        };
      });
    }

    hangoutConfig.allCharacters = charactersToStore;
    hangoutConfig.isMultiCharacter = true;
    hangoutConfig.characterCount = charactersToStore.length;

    const primaryIndex = charactersToStore.findIndex(c => String(c.id) === String(conversation.characterId));
    if (primaryIndex !== -1) {
      hangoutConfig.primaryCharacterIndex = primaryIndex;
    }
  }

  if (!hangoutConfig.scenarioId) {
    hangoutConfig.scenarioId = getScenarioIdFromHangoutConfig(hangoutConfig);
  }

  return normalizeHangoutScenarioConfig(hangoutConfig).config;
};

export const buildNewConversationConfig = ({
  scenarioId,
  character,
  carouselCharacters,
  selectedOutfitId,
  isPrivateScenario,
  scenarioTime,
  loc_id,
  selectedScenario,
  entrypoint = null
}) => {
  const timeOfDay = scenarioTime ? getTimeDefinitionService().getTimeOfDayFromNumericSlot(scenarioTime) : 'night';
  const resolvedEntrypoint = entrypoint
    || (isPrivateScenario ? HANGOUT_ENTRYPOINTS.RANKINGS_REGULAR_PRIVATE : HANGOUT_ENTRYPOINTS.RANKINGS_REGULAR_PUBLIC);

  const hangoutConfig = {
    scenarioId,
    isNewConversation: true,
    isPrivateScenario,
    timeOfDay: timeOfDay,
    greetingVariation: 0,
    greetingVariationCount: 1,
    entrypoint: resolvedEntrypoint,
    entryMode: 'new'
  };

  if (selectedScenario && typeof selectedScenario === 'object') {
    hangoutConfig.selectedScenario = { ...selectedScenario };
  } else if (scenarioId) {
    hangoutConfig.selectedScenario = { id: String(scenarioId) };
  }

  // Set loc_id from scenario when valid (non-zero, non-placeholder)
  const numericLocId = Number(loc_id);
  if (loc_id && numericLocId !== 0 && !String(loc_id).startsWith('ERROR')) {
    hangoutConfig.loc_id = String(loc_id);
  }

  if (carouselCharacters?.length > 1) {
    hangoutConfig.allCharacters = carouselCharacters.map(char => ({
      id: char.id,
      name: char.name,
      rank: char.rank,
      modalImage: char.modalImage,
      outfitId: char.outfitId
    }));
    const primaryCharacterIndex = hangoutConfig.allCharacters.findIndex(
      c => String(c.id) === String(character?.id)
    );
    hangoutConfig.primaryCharacterIndex = primaryCharacterIndex >= 0 ? primaryCharacterIndex : 0;
  } else {
    hangoutConfig.allCharacters = [{
      id: character?.id,
      name: character?.name,
      outfitId: selectedOutfitId || 0,
      modalImage: character?.modalImage || null
    }];
  }

  if (!hangoutConfig.scenarioId) {
    hangoutConfig.scenarioId = getScenarioIdFromHangoutConfig(hangoutConfig);
  }

  return normalizeHangoutScenarioConfig(hangoutConfig).config;
};
